import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Label } from "@/components/ui/label";
import { Check, Layers } from "lucide-react";

/**
 * Multi-select chip UI for tagging a master (Account/Category/Party/SubCategory)
 * to one or more Investments (projects).
 *
 *  value   : string[]                  — currently selected investment ids
 *  onChange: (string[]) => void        — called with updated ids
 *  label   : string (optional)         — field label; default "Applicable Investments"
 *  hint    : string (optional)         — helper text; default explains "empty = all"
 *  testId  : string (optional)         — for automation
 */
export default function InvestmentTagsField({
  value = [],
  onChange,
  label = "Applicable Investments",
  hint = "Leave empty to make this available on all projects.",
  testId = "investment-tags",
}) {
  const [investments, setInvestments] = useState([]);

  useEffect(() => {
    api.get("/investments").then((r) => setInvestments(r.data || [])).catch(() => setInvestments([]));
  }, []);

  const toggle = (id) => {
    const set = new Set(value || []);
    if (set.has(id)) set.delete(id);
    else set.add(id);
    onChange(Array.from(set));
  };

  const clear = () => onChange([]);

  const allEmpty = !value || value.length === 0;

  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between">
        <Label className="flex items-center gap-1.5 text-[12px]">
          <Layers className="h-3.5 w-3.5 text-slate-400" strokeWidth={1.75} />
          {label}
        </Label>
        {!allEmpty && (
          <button
            type="button"
            onClick={clear}
            className="text-[10px] uppercase tracking-wider text-slate-400 hover:text-red-600"
            data-testid={`${testId}-clear`}
          >
            Clear
          </button>
        )}
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {investments.length === 0 && (
          <div className="text-[11px] text-slate-400">No investments yet.</div>
        )}
        {investments.map((inv) => {
          const on = (value || []).includes(inv.id);
          return (
            <button
              type="button"
              key={inv.id}
              onClick={() => toggle(inv.id)}
              data-testid={`${testId}-chip-${inv.id}`}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${
                on
                  ? "bg-red-600 text-white border-red-600"
                  : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
              }`}
            >
              {on && <Check className="h-3 w-3" strokeWidth={2.5} />}
              {inv.name}
            </button>
          );
        })}
      </div>
      <div className="mt-1.5 text-[10px] text-slate-400">
        {allEmpty ? hint : `Tagged to ${value.length} project${value.length > 1 ? "s" : ""}.`}
      </div>
    </div>
  );
}
